import React, { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Layout/Sidebar";
import Header from "../Layout/Header";
import Swal from "sweetalert2";
import axios from "axios";
import "./AddModule.css";
import { API_BASE } from "../../../ApiUrl";

const API_URL = `${API_BASE}/modules/`;

const emptyModule = {
  code: "",
  name: "",
  description: "",
  is_active: true
};

const AddModule = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const editModule = location.state?.editModule || null;
  const isEditMode = Boolean(editModule?.id);

  const [modules, setModules] = useState(() => {
    if (editModule) {
      return [
        {
          code: editModule.code || "",
          name: editModule.name || "",
          description: editModule.description || "",
          is_active: editModule.is_active ?? true
        }
      ];
    }

    return [{ ...emptyModule }];
  });

  const [loading, setLoading] = useState(false);

  const handleChange = (index, e) => {
    const { name, value, type, checked } = e.target;

    setModules((prev) =>
      prev.map((mod, i) =>
        i === index
          ? {
              ...mod,
              [name]: type === "checkbox" ? checked : value
            }
          : mod
      )
    );
  };

  const addModule = () => {
    if (isEditMode) return;
    setModules((prev) => [...prev, { ...emptyModule }]);
  };

  const removeModule = (index) => {
    if (isEditMode || modules.length === 1) return;
    setModules((prev) => prev.filter((_, i) => i !== index));
  };

  const validateRow = (mod, index) => {
    const label = `Module ${index + 1}`;

    if (!mod.code.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Code for ${label}.`
      });
      return false;
    }

    if (!mod.name.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Required Field Missing",
        text: `Please provide a Name for ${label}.`
      });
      return false;
    }

    if (mod.code.trim().length > 50) {
      Swal.fire({
        icon: "warning",
        title: "Code Too Long",
        text: `Code for ${label} must be 50 characters or fewer.`
      });
      return false;
    }

    if (mod.name.trim().length > 255) {
      Swal.fire({
        icon: "warning",
        title: "Name Too Long",
        text: `Name for ${label} must be 255 characters or fewer.`
      });
      return false;
    }

    return true;
  };

  const buildPayload = (mod) => ({
    code: mod.code.trim(),
    name: mod.name.trim(),
    description: mod.description.trim(),
    is_active: mod.is_active
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    for (let i = 0; i < modules.length; i++) {
      if (!validateRow(modules[i], i)) return;
    }

    // Check duplicate codes within the form itself
    const codes = modules.map((m) => m.code.trim().toUpperCase());
    const duplicateIndex = codes.findIndex(
      (code, i) => codes.indexOf(code) !== i
    );

    if (duplicateIndex !== -1) {
      Swal.fire({
        icon: "warning",
        title: "Duplicate Code",
        text: `Code "${modules[duplicateIndex].code.trim()}" is used more than once.`
      });
      return;
    }

    try {
      setLoading(true);

      if (isEditMode) {
        const payload = buildPayload(modules[0]);

        console.log("Update Module Payload:", payload);

        const response = await axios.put(
          `${API_URL}${editModule.id}/`,
          payload,
          { headers: { "Content-Type": "application/json" } }
        );

        console.log("Update Module Response:", response.data);

        await Swal.fire({
          icon: "success",
          title: "Module Updated Successfully",
          text: "Module has been updated successfully.",
          confirmButtonText: "OK"
        });

        navigate("/admin/modules");
        return;
      }

      const payload = modules.map(buildPayload);

      console.log("Create Modules Payload:", payload);

      const response = await axios.post(API_URL, payload, {
        headers: { "Content-Type": "application/json" }
      });

      console.log("Create Modules Response:", response.data);

      await Swal.fire({
        icon: "success",
        title: "Modules Created Successfully",
        text: `${modules.length} module${
          modules.length > 1 ? "s" : ""
        } created successfully.`,
        confirmButtonText: "OK"
      });

      navigate("/admin/modules");
    } catch (error) {
      console.error(
        isEditMode ? "Update Module Error:" : "Create Modules Error:",
        error
      );

      let errorMessage = isEditMode
        ? "Unable to update module."
        : "Unable to create modules.";

      if (error.response?.data) {
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else if (error.response.data.errors) {
          errorMessage = Object.entries(error.response.data.errors)
            .map(
              ([field, errors]) =>
                `${field}: ${
                  Array.isArray(errors) ? errors.join(", ") : errors
                }`
            )
            .join(" | ");
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      }

      Swal.fire({
        icon: "error",
        title: isEditMode ? "Update Failed" : "Save Failed",
        text: errorMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/admin/modules");
  };

  return (
    <div className="app-layout">
      <Sidebar />

      <div className="main-content">
        <Header />

        <div className="module-page">
          <div className="module-page-header">
            <div>
              <h2>{isEditMode ? "Edit Module" : "Create Module"}</h2>
              <p>
                {isEditMode
                  ? "Update platform module information."
                  : "Add one or multiple platform modules."}
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="module-card">
              <div className="module-form-body">
                {modules.map((mod, index) => (
                  <div className="module-record" key={index}>
                    <div className="module-record-header">
                      <div>
                        <h4>
                          {isEditMode
                            ? "Module Details"
                            : `Module ${index + 1}`}
                        </h4>
                      </div>

                      {!isEditMode && modules.length > 1 && (
                        <button
                          type="button"
                          className="module-remove-btn"
                          onClick={() => removeModule(index)}
                        >
                          Remove
                        </button>
                      )}
                    </div>

                    <div className="module-form-grid">
                      <div className="module-form-group">
                        <label>
                          Code <span>*</span>
                        </label>
                        <input
                          type="text"
                          name="code"
                          value={mod.code}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="e.g. SMS, LPMS, TPI"
                          maxLength={50}
                          required
                        />
                      </div>

                      <div className="module-form-group">
                        <label>
                          Name <span>*</span>
                        </label>
                        <input
                          type="text"
                          name="name"
                          value={mod.name}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Enter module name"
                          maxLength={255}
                          required
                        />
                      </div>

                      <div className="module-form-group">
                        <label>Description</label>
                        <input
                          type="text"
                          name="description"
                          value={mod.description}
                          onChange={(e) => handleChange(index, e)}
                          placeholder="Enter module description (optional)"
                        />
                      </div>

                      <div className="module-status-field">
                        <label>Module Status</label>

                        <label className="module-switch">
                          <input
                            type="checkbox"
                            name="is_active"
                            checked={mod.is_active}
                            onChange={(e) => handleChange(index, e)}
                          />
                          <span className="module-slider"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}

                {!isEditMode && (
                  <button
                    type="button"
                    className="module-add-btn"
                    onClick={addModule}
                  >
                    <span>+</span>
                    Add Another Module
                  </button>
                )}
              </div>
            </div>

            <div className="module-form-actions">
              <button
                type="button"
                className="module-cancel-btn"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="module-save-btn"
                disabled={loading}
              >
                {loading
                  ? isEditMode
                    ? "Updating..."
                    : "Saving..."
                  : isEditMode
                  ? "Update Module"
                  : `Save ${modules.length} Module${
                      modules.length > 1 ? "s" : ""
                    }`}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddModule;